import { useEffect, useState } from 'preact/hooks'
import { getLocalData, clearLocalData } from '../services/storage'
import { setEmail, getEmail, getKeywords, lockVisibleHeaders } from '../utils'
import SendIcon from './icons/PlaneIcon'
import Button from './ui/Button'
import LinkButton from './ui/LinkButton'
import Spinner from './ui/Spinner'
import AcceptPolicy from './AcceptPolicy'
import { useTranslation } from 'react-i18next'
import fomService from '../services/fomService'
import { useContext } from 'preact/hooks'
import { AppContext } from '../main'
import ResetIcon from './icons/ResetIcon'
import { track } from '../services/analytics'
import { EMAIL_REG_EXP } from '../constants'
import useEmailControl from '../hooks/useEmailControl'

export default function Submit({ setStatus }) {
  const { t } = useTranslation()
  const { playbookId, headersCount } = useContext(AppContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { hasEmailFilled, isPolicyAccepted, requiredEmail, setHasEmailFilled, setIsPolicyAccepted, setRequiredEmail } =
    useEmailControl()

  const handleClick = async () => {
    if (isSubmitting || !isPolicyAccepted) {
      return
    }

    const actionCard = getLocalData(playbookId)

    let email = getEmail(actionCard)

    if (!email && hasEmailFilled) {
      setEmail(actionCard, requiredEmail)
      email = requiredEmail
    }

    if (!email) return

    try {
      setIsSubmitting(true)

      const keywords = getKeywords(actionCard)
      const lastActiveSeparator = actionCard.sections.find(s => s.id === actionCard.lastActiveSeparator)?.name
      const score = Math.round(actionCard.score / 2)
      lockVisibleHeaders(actionCard, headersCount)

      let playCreationResponse = await fomService.createPlay(
        playbookId,
        email,
        keywords,
        score,
        lastActiveSeparator,
        actionCard,
      )
      console.log('Created Play:', playCreationResponse)
      setIsSubmitted(true)
      setStatus({
        text: t('submit.success', 'Your data has been submitted successfully'),
        type: 'success',
      })
      track('ac_submitted', {
        playbookId: playbookId,
        keywords: keywords,
      })
    } catch (e) {
      console.error(e)
      setStatus({
        text: t('submit.error', 'Failed to submit your data. Please try again'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRestart = () => {
    clearLocalData(playbookId)
    window.location.href = window.location.href
  }

  return (
    <div className="flex flex-col gap-2 mt-2">
      <AcceptPolicy setIsPolicyAccepted={setIsPolicyAccepted} setHasEmailFilled={setHasEmailFilled} />
      <EmailInput
        isVisible={!hasEmailFilled && isPolicyAccepted}
        email={requiredEmail}
        onChange={value => {
          setHasEmailFilled(!!EMAIL_REG_EXP.test(value))
          setRequiredEmail(value)
        }}
      />
      <div
        className="flex gap-2 items-center justify-center ml-8"
        title={!isPolicyAccepted ? t('submit.tooltip', 'Please accept the terms') : ''}
      >
        {!isSubmitted && (
          <Button
            label={t('submit.send', 'Send')}
            disabled={isSubmitting || !isPolicyAccepted || !hasEmailFilled}
            icon={<SendIcon size="16" />}
            onClick={handleClick}
          ></Button>
        )}
        {isSubmitted && (
          <Button
            label={t('submit.resend', 'Resend')}
            disabled={isSubmitting || !isPolicyAccepted || !hasEmailFilled}
            icon={<SendIcon size="16" />}
            onClick={handleClick}
          ></Button>
        )}
        <Spinner isActive={isSubmitting} />
        {isSubmitted && (
          <LinkButton
            label={t('submit.restart', 'Restart')}
            icon={<ResetIcon size="16" />}
            onClick={handleRestart}
          ></LinkButton>
        )}
      </div>
    </div>
  )
}

const EmailInput = ({ isVisible, onChange, email }) => {
  const { t } = useTranslation()
  const [persisted, setPersisted] = useState(false)

  const handleChange = e => {
    onChange(e.target.value)
  }

  useEffect(() => {
    if (isVisible) {
      setPersisted(true)
    }
  }, [isVisible])

  useEffect(() => {
    const callBack = ({ detail }) => {
      const email = getEmail(detail)
      onChange(email)
    }

    window.addEventListener('actioncard-change', callBack)

    return () => {
      window.removeEventListener('actioncard-change', callBack)
    }
  }, [email])

  return (
    <div
      className={`
          flex justify-center overflow-hidden transition-all ${isVisible || persisted ? 'h-[58px]' : 'h-0'}
        `}
    >
      <div className={`flex flex-col py-[8px]`}>
        <input
          onChange={handleChange}
          placeholder={t('submit.enterEmailLabel', 'Please enter your email')}
          id="emailInput"
          value={email}
          className={`
            relative min-h-[38px] border-[#E2E2E2] border-solid dark:border-[#353535] 
           bg-white dark:bg-black border-[1px] rounded-[6px] dark:text-white px-[8px] min-w-[320px] outline-none
          `}
          type="text"
        />
      </div>
    </div>
  )
}
